import { callFcmNotify, callSendNotification, callSendOrderNotification } from '@/utils/api.util';
import { supabase } from '@/integrations/supabase/client';

// Types for notification payloads
export interface FcmNotificationPayload {
  token: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  priority?: 'high' | 'normal' | 'low';
  requireInteraction?: boolean;
  silent?: boolean;
}

export interface OrderNotificationData {
  orderId: string;
  storeId: string;
  tableId: string;
  tableName: string;
  tableNumber: number;
  status: string;
  totalAmount: number;
  action: string;
  items?: Array<{ name: string; quantity: number }>;
  specialInstructions?: string;
  paymentMethod?: string;
  tipAmount?: number;
  tipPercentage?: number;
  previousStatus?: string;
  navigationRoute: string;
}

export interface ChatNotificationData {
  messageId: string;
  senderId: string;
  senderName: string;
  storeId: string;
  messageType: string;
  content: string;
  replyToMessageId?: string;
  navigationRoute: string;
}

// FCM Token management
export const updateFcmToken = async (userId: string, fcmToken: string) => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ fcm_token: fcmToken })
      .eq('id', userId);

    if (error) {
      console.error('Error updating FCM token:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating FCM token:', error);
    return { success: false, error: 'Failed to update FCM token' };
  }
};

export const removeFcmToken = async (userId: string) => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ fcm_token: null })
      .eq('id', userId);

    if (error) {
      console.error('Error removing FCM token:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error removing FCM token:', error);
    return { success: false, error: 'Failed to remove FCM token' };
  }
};

// Send FCM notification
export const sendFcmNotification = async (payload: FcmNotificationPayload) => {
  try {
    const result = await callFcmNotify(payload);
    
    if (!result.success) {
      console.error('FCM notification failed:', result.error);
      return { success: false, error: result.error };
    }

    console.log('FCM notification sent successfully:', result.data);
    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error sending FCM notification:', error);
    return { success: false, error: 'Failed to send FCM notification' };
  }
};

// Send notification to specific user
export const sendNotificationToUser = async (
  userId: string,
  title: string,
  body: string,
  data?: Record<string, unknown>
) => {
  try {
    const result = await callSendNotification({
      userId,
      title,
      body,
      data,
    });

    if (!result.success) {
      console.error('User notification failed:', result.error);
      return { success: false, error: result.error };
    }

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error sending user notification:', error);
    return { success: false, error: 'Failed to send user notification' };
  }
};

// Send order notification
export const sendOrderNotification = async (
  orderId: string,
  userId: string,
  title: string,
  body: string,
  data?: Record<string, unknown>
) => {
  try {
    const result = await callSendOrderNotification({
      orderId,
      userId,
      title,
      body,
      data,
    });

    if (!result.success) {
      console.error('Order notification failed:', result.error);
      return { success: false, error: result.error };
    }

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error sending order notification:', error);
    return { success: false, error: 'Failed to send order notification' };
  }
};

// Send notification to store staff
export const sendNotificationToStoreStaff = async (
  storeId: string,
  title: string,
  body: string,
  data?: Record<string, unknown>,
  excludeUserId?: string
) => {
  try {
    // Fetch store staff with FCM tokens
    const { data: staff, error: staffError } = await supabase
      .from('store_employees')
      .select(`
        user_id,
        users!inner(
          id,
          fcm_token,
          push_orders,
          push_updates,
          push_marketing
        )
      `)
      .eq('store_id', storeId);

    if (staffError) {
      console.error('Error fetching store staff:', staffError);
      return { success: false, error: staffError.message };
    }

    if (!staff || staff.length === 0) {
      console.log('No staff found for store:', storeId);
      return { success: true, data: { message: 'No staff found' } };
    }

    // Send notification to each staff member
    const results = [];
    for (const employee of staff) {
      const user = employee.users;
      
      // Skip if user is excluded
      if (excludeUserId && user.id === excludeUserId) {
        continue;
      }

      if (!user.fcm_token) {
        console.log(`No FCM token for staff member ${user.id}`);
        continue;
      }

      // Check user's notification settings
      if (!shouldSendNotification(user, data?.notificationType as string)) {
        console.log(`Staff member ${user.id} has disabled notifications`);
        continue;
      }

      const result = await sendFcmNotification({
        token: user.fcm_token,
        title,
        body,
        data,
        priority: 'high',
      });

      results.push({ userId: user.id, result });
    }

    return { success: true, data: { results } };
  } catch (error) {
    console.error('Error sending notifications to store staff:', error);
    return { success: false, error: 'Failed to send staff notifications' };
  }
};

// Send notification to store owner
export const sendNotificationToStoreOwner = async (
  storeId: string,
  title: string,
  body: string,
  data?: Record<string, unknown>
) => {
  try {
    // Fetch store owner
    const { data: store, error: storeError } = await supabase
      .from('stores')
      .select(`
        owner_id,
        users!inner(
          id,
          fcm_token,
          push_orders,
          push_updates,
          push_marketing
        )
      `)
      .eq('id', storeId)
      .single();

    if (storeError) {
      console.error('Error fetching store owner:', storeError);
      return { success: false, error: storeError.message };
    }

    if (!store || !store.users.fcm_token) {
      console.log('No FCM token for store owner');
      return { success: true, data: { message: 'No owner FCM token' } };
    }

    // Check owner's notification settings
    if (!shouldSendNotification(store.users, data?.notificationType as string)) {
      console.log('Store owner has disabled notifications');
      return { success: true, data: { message: 'Owner notifications disabled' } };
    }

    const result = await sendFcmNotification({
      token: store.users.fcm_token,
      title,
      body,
      data,
      priority: 'high',
    });

    return result;
  } catch (error) {
    console.error('Error sending notification to store owner:', error);
    return { success: false, error: 'Failed to send owner notification' };
  }
};

// Check if notification should be sent based on user settings
const shouldSendNotification = (
  user: { push_orders?: boolean | null; push_updates?: boolean | null; push_marketing?: boolean | null },
  notificationType?: string
): boolean => {
  // Default to true if no settings
  if (user.push_orders === null && user.push_updates === null && user.push_marketing === null) {
    return true;
  }

  switch (notificationType) {
    case 'order_placed':
    case 'order_status_updated':
    case 'order_ready':
    case 'order_completed':
    case 'order_cancelled':
      return user.push_orders !== false;
    
    case 'new_message':
    case 'message_reply':
    case 'reservation_created':
    case 'reservation_confirmed':
    case 'reservation_cancelled':
    case 'table_assigned':
    case 'table_unassigned':
    case 'inventory_low_stock':
    case 'inventory_out_of_stock':
    case 'inventory_critical':
      return user.push_updates !== false;
    
    default:
      return true;
  }
};

// Test notification function
export const sendTestNotification = async (fcmToken: string) => {
  return sendFcmNotification({
    token: fcmToken,
    title: 'Test Notification',
    body: 'This is a test notification from CultryPay',
    data: {
      notificationType: 'test',
      timestamp: Date.now(),
      navigationRoute: '/dashboard',
    },
    priority: 'high',
  });
}; 